"use client";

import React, { createContext, useContext, ReactNode, useEffect, useState } from "react";
import {
  ABTestingService,
  getABTestingService,
} from "@/services/ABTestingService/ABTestingService";
import { ABCampaigns } from "@/services/ABTestingService/types";
import { isClient } from "@/utils/browser";

interface ABTestingContextType {
  service: ABTestingService;
  isReady: boolean;
  isInitialized: boolean;
  getVariant: (campaignKey: string) => string | undefined;
  isVariantActive: (campaignKey: string, variantName: string) => boolean;
  trackEvent: (eventName: string, properties?: Record<string, any>) => void;
  getAllCampaigns: () => ABCampaigns;
  error: string | null;
}

const ABTestingContext = createContext<ABTestingContextType | undefined>(undefined);

interface ABTestingProviderProps {
  children: ReactNode;
}

export const ABTestingProvider: React.FC<ABTestingProviderProps> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [service] = useState(() => getABTestingService()); // Singleton instance

  useEffect(() => {
    const initializeABTest = async () => {
      if (!isClient()) {
        return; // Skip initialization on server side
      }

      try {
        const hostname = window.location.hostname;
        await service.initialize(hostname);

        setIsInitialized(true);

        // Wait for the service to be ready
        let attempts = 0;
        const maxAttempts = 20; // 2 seconds timeout

        const checkReady = () => {
          if (service.isReady()) {
            setIsReady(true);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkReady, 100);
          } else {
            if (process.env.NEXT_PUBLIC_ENV !== "prod") {
              console.warn("AB Test Service timeout, but continuing");
            }
            setIsReady(true);
          }
        };

        checkReady();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        console.error("AB Test Service initialization failed:", errorMessage);
        setError(errorMessage);
        setIsReady(true); // Allow app to continue even if AB test fails
      }
    };

    initializeABTest();
  }, [service]);

  const getVariant = (campaignKey: string): string | undefined => {
    return service.getVariant(campaignKey);
  };

  const isVariantActive = (campaignKey: string, variantName: string): boolean => {
    return service.isVariantActive(campaignKey, variantName);
  };

  const trackEvent = (eventName: string, properties?: Record<string, any>): void => {
    service.trackEvent(eventName, properties);
  };

  const getAllCampaigns = (): ABCampaigns => {
    return service.getAllCampaigns();
  };

  const contextValue: ABTestingContextType = {
    service,
    isReady,
    isInitialized,
    getVariant,
    isVariantActive,
    trackEvent,
    getAllCampaigns,
    error,
  };

  return <ABTestingContext.Provider value={contextValue}>{children}</ABTestingContext.Provider>;
};

export const useABTest = (): ABTestingContextType => {
  const context = useContext(ABTestingContext);
  if (!context) {
    throw new Error("useABTest must be used within ABTestingProvider");
  }
  return context;
};

export const useABTestCampaign = (
  campaignKey: string,
  defaultVariant: string = "control",
  trackView: boolean = false
) => {
  const { getVariant, isVariantActive, trackEvent, isReady } = useABTest();
  const [variant, setVariant] = useState<string>(defaultVariant);

  useEffect(() => {
    if (!isReady) return;

    const activeVariant = getVariant(campaignKey) || defaultVariant;
    setVariant(activeVariant);

    if (trackView) {
      trackEvent(`${campaignKey}_view`, {
        variant: activeVariant,
        campaign: campaignKey,
      });
    }
  }, [campaignKey, defaultVariant, trackView, isReady, getVariant, trackEvent]);

  return {
    variant,
    isReady,
    isVariant: (variantName: string) => isVariantActive(campaignKey, variantName),
    trackEvent: (eventName: string, properties?: Record<string, any>) => {
      trackEvent(eventName, {
        campaign: campaignKey,
        variant,
        ...properties,
      });
    },
  };
};
